import type { LabelUnit } from '../BatchTracking/types';

/** A unit the user can type a quantity in: `decimals` is how many places it has over the item's own unit (kg over g). */
export type UnitChoice = { label: string; decimals: 0 | 3 };

const CHOICES: Record<LabelUnit, [UnitChoice, ...UnitChoice[]]> = {
  g: [
    { label: 'kg', decimals: 3 },
    { label: 'g', decimals: 0 }
  ],
  ml: [
    { label: 'L', decimals: 3 },
    { label: 'ml', decimals: 0 }
  ],
  piece: [{ label: 'pcs', decimals: 0 }]
};

export const unitChoices = (unit: LabelUnit): [UnitChoice, ...UnitChoice[]] => CHOICES[unit];

export type QuantityProblem = 'invalid' | 'notPositive' | 'fractional';
type Quantity = { quantity: number } | { problem: QuantityProblem };

const NUMBER = /^(-?)(?=[\d.,]*\d)(\d*)(?:[.,](\d*))?$/;

/** The text as a whole number of the item's own unit, read digit by digit so 1.005 kg is 1005 g and not 1004.99…. */
export const parseQuantity = (text: string, { decimals }: UnitChoice): Quantity => {
  const match = NUMBER.exec(text.trim());
  if (match === null) {
    return { problem: 'invalid' };
  }
  const [, sign, whole = '', fraction = ''] = match;
  if (sign === '-') {
    return { problem: 'notPositive' };
  }
  if (/[1-9]/.test(fraction.slice(decimals))) {
    return { problem: 'fractional' };
  }
  const quantity = Number(whole + fraction.slice(0, decimals).padEnd(decimals, '0'));
  return quantity === 0 ? { problem: 'notPositive' } : { quantity };
};

/** The unit and text a stored quantity opens in: whole kilograms or litres and above read in them, anything less in g or ml. */
export const fieldsOf = (quantity: number, unit: LabelUnit): { choice: UnitChoice; text: string } => {
  const [large, small = large] = unitChoices(unit);
  return large.decimals > 0 && quantity >= 10 ** large.decimals
    ? { choice: large, text: String(quantity / 10 ** large.decimals) }
    : { choice: small, text: String(quantity) };
};

export const problemMessage = (problem: QuantityProblem, { label, decimals }: UnitChoice): string => {
  switch (problem) {
    case 'invalid': {
      return 'Enter the quantity as a number, like 2.5.';
    }
    case 'notPositive': {
      return 'The quantity must be more than zero.';
    }
    case 'fractional': {
      return decimals === 0
        ? `Use a whole number of ${label}.`
        : `Use up to ${decimals} decimals. The smallest amount is 0.001 ${label}.`;
    }
  }
};
